import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { DASHBOARD } from "@/constants/testIds";
import Modal from "@/components/Modal";

function CategoryForm({ initial, onSubmit, onCancel }) {
  const { t } = useI18n();
  const [name, setName] = useState(initial?.name || "");
  const [submitting, setSubmitting] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error(t("category_name"));
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({ name: trimmed });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handle} className="space-y-4">
      <div>
        <label className="label-overline block mb-1">{t("category_name")}</label>
        <input
          data-testid={DASHBOARD.categoryFormName}
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full border border-[var(--border)] px-3 py-2 font-display"
          placeholder={t("category_name")}
        />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          data-testid={DASHBOARD.categoryFormCancel}
          onClick={onCancel}
          className="border border-[var(--border)] px-4 py-2 text-sm hover:bg-[var(--bg)]"
        >
          {t("cancel")}
        </button>
        <button
          type="submit"
          disabled={submitting}
          data-testid={DASHBOARD.categoryFormSubmit}
          className="bg-[var(--text-primary)] text-white px-5 py-2 font-display font-bold tracking-wide hover:bg-[var(--primary)] disabled:opacity-50"
        >
          {t("save")}
        </button>
      </div>
    </form>
  );
}

export default function CategoriesPanel({ onCategoriesChanged }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/categories`, { withCredentials: true });
      setItems(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (payload) => {
    try {
      if (editing?.id) {
        await axios.put(`${API}/categories/${editing.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/categories`, payload, { withCredentials: true });
      }
      toast.success(t("saved_ok"));
      setOpen(false);
      setEditing(null);
      await load();
      onCategoriesChanged && onCategoriesChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const toggle = async (cat) => {
    try {
      await axios.patch(`${API}/categories/${cat.id}/toggle-active`, null, { withCredentials: true });
      toast.success(t("state_changed_ok"));
      await load();
      onCategoriesChanged && onCategoriesChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const filtered = items.filter((c) => {
    if (statusFilter === "active") return c.is_active;
    if (statusFilter === "inactive") return !c.is_active;
    return true;
  });

  return (
    <div data-testid={DASHBOARD.categoriesList} className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-end gap-3">
          <div>
            <label className="label-overline block mb-1">{t("status")}</label>
            <select
              data-testid={DASHBOARD.categoryFilterStatus}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-[var(--border)] px-3 py-2 font-mono-tech text-sm bg-white"
            >
              <option value="all">{t("all_statuses")}</option>
              <option value="active">{t("active_only")}</option>
              <option value="inactive">{t("inactive_only")}</option>
            </select>
          </div>
        </div>
        <button
          data-testid={DASHBOARD.categoryNewBtn}
          onClick={() => {
            setEditing({});
            setOpen(true);
          }}
          className="bg-[var(--text-primary)] text-white px-5 py-2.5 font-display font-bold tracking-wide hover:bg-[var(--primary)] transition-colors"
        >
          + {t("new_category")}
        </button>
      </div>

      {loading ? (
        <div className="font-mono-tech text-sm text-[var(--text-secondary)] py-8 text-center">
          LOADING…
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-[var(--border)] bg-white py-16 text-center text-[var(--text-secondary)]">
          {t("no_categories")}
        </div>
      ) : (
        <div className="border border-[var(--border)] bg-white divide-y divide-[var(--border)]">
          {filtered.map((c) => (
            <div
              key={c.id}
              data-testid={DASHBOARD.categoryRow(c.id)}
              className={`grid grid-cols-12 gap-4 items-center px-5 py-4 hover:bg-[var(--bg)] ${
                c.is_active ? "" : "opacity-60"
              }`}
            >
              <div className="col-span-12 sm:col-span-7">
                <div className="font-display text-lg font-bold tracking-tight">{c.name}</div>
              </div>
              <div className="col-span-6 sm:col-span-2">
                <span
                  className={`font-mono-tech text-[0.65rem] tracking-wider px-2 py-1 ${
                    c.is_active
                      ? "bg-black text-white"
                      : "border border-[var(--border)] text-[var(--text-secondary)]"
                  }`}
                >
                  {c.is_active ? t("active").toUpperCase() : t("inactive").toUpperCase()}
                </span>
              </div>
              <div className="col-span-6 sm:col-span-3 flex justify-end gap-2">
                <button
                  data-testid={DASHBOARD.categoryEditBtn(c.id)}
                  onClick={() => {
                    setEditing(c);
                    setOpen(true);
                  }}
                  className="border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-[var(--surface-alt)]"
                >
                  {t("edit")}
                </button>
                <button
                  data-testid={DASHBOARD.categoryToggleBtn(c.id)}
                  onClick={() => toggle(c)}
                  className={`border px-3 py-1.5 text-sm transition-colors ${
                    c.is_active
                      ? "border-[var(--border)] hover:bg-[var(--bg)]"
                      : "border-black bg-black text-white hover:opacity-80"
                  }`}
                >
                  {c.is_active ? t("deactivate") : t("activate")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setEditing(null);
        }}
        title={editing?.id ? t("edit_category") : t("new_category")}
        testId="category-modal"
      >
        <CategoryForm
          initial={editing}
          onSubmit={submit}
          onCancel={() => {
            setOpen(false);
            setEditing(null);
          }}
        />
      </Modal>
    </div>
  );
}
